import { inject, Provider } from '@angular/core';
import { canManageSkillInference } from '@app/orgs/services/guards/manage-skill-inference.utils';
import { GdAccess } from '@app/reporting/gd-advanced-analytics/access/gd-access.models';
import { GD_ACCESS } from '@app/reporting/gd-advanced-analytics/access/gd-access.token';
import { LDFlagsService } from '@degreed/lxp/services';
import { AuthService } from '@degreed/lxp/services/authorization';

/** Root-provided `GD_ACCESS` from `authUser` and LD flags, computed once per session. */
export function provideGdAccess(): Provider {
  return {
    provide: GD_ACCESS,
    useFactory: (): GdAccess => {
      const authUser = inject(AuthService).authUser;
      const ldFlags = inject(LDFlagsService);
      const reporting = authUser?.permissions?.reporting;
      const flags = ldFlags.reporting;

      const orgId = authUser?.defaultOrgId;
      const isAdmin = !!reporting?.isAnalyticsAdmin;
      const isScoped = !!reporting?.isScopedAnalyticsViewer;
      const selfServiceOn = !!flags.enableSelfServiceAnalytics;
      const scopedCreationOn = !!flags.enableScopedAnalyticsDashboardCreation;

      const canCreateDashboard = isAdmin || (isScoped && scopedCreationOn);

      return {
        orgId,
        canViewAnalytics: selfServiceOn && (isAdmin || isScoped),
        canUseDesigner: selfServiceOn && canCreateDashboard,
        canCreateDashboard,
        isViewOnly: isScoped && !isAdmin && !scopedCreationOn,
        canManageSettings:
          selfServiceOn && !!reporting?.canManageAnalyticsStudioSettings,
        canManagePreferences: selfServiceOn && isAdmin,
        // AnalyticsAdmin only, deliberately no feature flag.
        canManageVisualizations: isAdmin,
        // Mirrors the server's three checks, in order: org flag (via LD), AnalyticsAdmin,
        // ManageReportingFTPScheduler.
        canScheduleExports:
          !!flags.enableScheduledExports &&
          isAdmin &&
          !!reporting?.canScheduleFTPReports,
        canViewSkillInference: canManageSkillInference(
          authUser,
          orgId,
          ldFlags
        ),
      };
    },
  };
}
